import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import InitialTestReportModal from '../components/InitialTestReportModal';
import Sidebar from '../components/Sidebar';
import LanguageSelector from '../components/LanguageSelector';
import ResetPassword from '../components/ResetPassword';
import FloatingRoleButtons from '../components/FloatingRoleButtons';
import ChatFloatingButton from '../components/ChatFloatingButton';
import ProgressFloatingPanel from '../components/ProgressFloatingPanel';
import ChatHubPanel from '../components/ChatHubPanel';
import { getPublicSetting, getShopItemById, getTotalUnreadCount } from '../api/supabaseAPI';
import { supabase } from '../supabaseClient';
import usePresenceGateway from '../hooks/usePresenceGateway';

const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

const buildWallpaperUrl = (baseUrl, path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  if (!baseUrl) return path;
  return `${baseUrl}/${String(path).replace(/^\/+/, '')}`;
};

const MainLayout = ({
  children,
  showInitialTestReport,
  initialTestResult,
  onReportRead,
  showPasswordReset,
  onPasswordUpdated,
  onLogout,
  hasCompletedTest,
  userData,
  isAdmin, // ✨ Received from props
  isTutor, // ✨ Received from props
}) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const location = useLocation();
  const currentUserId = userData?.id || '';
  const wallpaperItemId = userData?.wallpaper_item_id || null;
  const queryClient = useQueryClient();
  const presence = usePresenceGateway(currentUserId, !!currentUserId);

  // ✨ Check if we are in Tutor Workspace mode
  const isTutorMode = location.pathname.startsWith('/tutor/workspace');

  const { data: totalUnread = 0 } = useQuery({
    queryKey: ['chat_unread_total', currentUserId],
    queryFn: () => getTotalUnreadCount(currentUserId),
    enabled: !!currentUserId,
    refetchInterval: 5000,
    staleTime: 3000,
  });

  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const { data: wallpaperItem } = useQuery({
    queryKey: ['shop_item', wallpaperItemId],
    queryFn: () => getShopItemById(wallpaperItemId),
    enabled: !!wallpaperItemId,
    staleTime: 1000 * 60 * 5,
  });

  const wallpaperBaseUrl = (R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '').replace(/\/$/, '');
  const wallpaperUrl = buildWallpaperUrl(wallpaperBaseUrl, wallpaperItem?.path || '');

  useEffect(() => {
    if (!currentUserId) return undefined;

    const channel = supabase
      .channel(`chat-unread-total-live-${currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'private_chat',
        },
        (payload) => {
          const row = payload?.new;
          if (!row?.session_id) return;
          if (String(row.sender || '').toLowerCase() === currentUserId.toLowerCase()) return;

          queryClient.setQueryData(['chat_unread_total', currentUserId], (prev) => Number(prev || 0) + 1);
          queryClient.invalidateQueries({ queryKey: ['chat_unread_total', currentUserId], exact: true });
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_participants',
        },
        (payload) => {
          const row = payload?.new;
          if (!row?.session_id) return;
          if (String(row.involver || '').toLowerCase() !== currentUserId.toLowerCase()) return;

          queryClient.invalidateQueries({ queryKey: ['chat_unread_total', currentUserId], exact: true });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUserId, queryClient]);

  useEffect(() => {
    const handleOpenChatPanel = () => {
      setIsChatOpen(true);
    };

    window.addEventListener('theralingua:open-chat-panel', handleOpenChatPanel);
    return () => {
      window.removeEventListener('theralingua:open-chat-panel', handleOpenChatPanel);
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    if (wallpaperUrl) {
      root.setAttribute('data-has-wallpaper', 'true');
    } else {
      root.removeAttribute('data-has-wallpaper');
    }

    return () => {
      root.removeAttribute('data-has-wallpaper');
    };
  }, [wallpaperUrl]);

  return (
    <div className={`app-shell ${wallpaperUrl ? 'has-wallpaper' : ''}`}>
      {wallpaperUrl && (
        <div className="app-wallpaper-layer" aria-hidden="true">
          <img className="app-wallpaper-image" src={wallpaperUrl} alt="" />
        </div>
      )}
      <Sidebar
        onLogout={onLogout}
        hasCompletedTest={hasCompletedTest}
        userData={userData}
      />
      <div className={`app-main ${wallpaperUrl ? 'has-wallpaper' : ''}`}>
        {showInitialTestReport && (
          <InitialTestReportModal report={initialTestResult} onConfirm={onReportRead} />
        )}
        {showPasswordReset && <ResetPassword onPasswordUpdated={onPasswordUpdated} />}
        {children}
        {!isTutorMode && <ProgressFloatingPanel userData={userData} />}
        <ChatFloatingButton
          onClick={() => setIsChatOpen((prev) => !prev)}
          title="Open chat"
          isActive={isChatOpen}
          unreadCount={totalUnread}
        />
        <ChatHubPanel
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
          userData={userData}
          presence={presence}
        />
        <LanguageSelector />
        <FloatingRoleButtons isAdmin={isAdmin} isTutor={isTutor} />
      </div>
    </div>
  );
};

export default MainLayout;
